import React from 'react';
import _capitalize from 'lodash/capitalize';
import styled from 'styled-components';
import { Alert, Skeleton } from 'antd';
import { Button } from '@hulu/react-style-components';
import isEmpty from 'lodash/isEmpty';

import { AuctionErrorBoundary } from '../AuctionErrorBoundary';
import { formatDate } from '../../../utils';
import { TIME_FRAMES, DURATION_OPTIONS, MAGIC_WORDS_OPTIONS } from '../../../constants';
import { FORM_FIELDS, RESULT_OPTIONS, INVENTORY_TYPES, LIMITED_AD_TRACKING_OPTIONS } from '../constants';
import { Card, StyledCardItem } from './Card';
import { StyledDrawer, StyledCloseButton } from './style';

const StyledCardItemTitle = styled.div`
  min-width: 210px;
  max-width: 210px;
`;
export const StyledContent = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: stretch;
  overflow-y: auto;
  overflow-x: hidden;
  height: 100%;
  .action-rerun {
    box-sizing: border-box;
    box-shadow: 4px 0 16px rgba(0, 0, 0, 0.08);
    height: 72px;
    padding: 13px 16px 13px 0;
    text-align: end;
    > a {
      margin: 0;
    }
  }
  .ant-skeleton-title {
    margin-bottom: 0;
    margin-top: 8px;
  }
  .ant-skeleton-paragraph {
    margin-bottom: 8px;
    margin-top: 0;
  }
  .ant-skeleton-content {
    padding: 0 8px;
  }
  .ant-skeleton-content .ant-skeleton-paragraph > li + li {
    margin-top: 8px;
  }
  .ant-skeleton-content .ant-skeleton-title + .ant-skeleton-paragraph {
    margin-top: 8px;
  }
`;

const getLabelByValue = (options, value) => options?.find((item) => item?.value === value)?.label;

const sanitizeEmptyValue = (obj = {}) => Object.fromEntries(Object.entries(obj).filter(([_, v]) => !isEmpty(v)));

const CardItem = ({ title, data }) =>
  data ? (
    <StyledCardItem>
      <StyledCardItemTitle>{title}</StyledCardItemTitle>
      <span>{data}</span>
    </StyledCardItem>
  ) : (
    <></>
  );

export const SnapshotParametersInfo = ({
  isOpen,
  onClose,
  data,
  isLoading,
  error,
  snapshotId,
  onRerunSnapshot,
  profileTypes,
}) => {
  const { supplyParameters, demandParameters, name, status, date, timeAllowedBeforeStopRunning } = data;
  const {
    appName,
    publisher,
    channel,
    deviceFamily,
    inventoryType,
    ifaType,
    vdm,
    sessionId,
    magicWordSearch,
    selectionSearch,
    domain,
    dnt,
    userAgent,
    series,
    assetId,
    contentPartner,
    pod,
    brand,
    deviceCategory,
    distributor,
    distributionPartner,
    formFactor,
    market,
    sovereignCountries,
    supplyAdUnitId,
    podPosition,
    entityType,
    sportsLeague,
    ipAddress,
    experimentId,
  } = supplyParameters || {};
  const {
    result,
    advertiser,
    creativeDuration,
    creativeId,
    mediaFileUrl,
    winningAuction,
    adPartnerDsp,
    receivedBidDealId,
    bidState,
    externalDealId,
    internalDealId,
    adProduct,
    adProductType,
  } = demandParameters || {};
  const demandParametersCardData = sanitizeEmptyValue({
    winningAuction,
    adPartnerDsp,
    externalDealId,
    internalDealId,
    bidState,
    creativeDuration,
    mediaFileUrl,
    receivedBidDealId,
    adProduct,
    adProductType,
    advertiser,
    creativeId,
  });
  const appParametersCardData = sanitizeEmptyValue({
    domain,
    inventoryType,
    deviceFamily,
    brand,
    formFactor,
    deviceCategory,
    ifaType,
    userAgent,
    dnt,
    ipAddress,
  });
  const contentParametersCardData = sanitizeEmptyValue({
    channel,
    series,
    assetId,
    contentPartner,
    vdm,
    supplyAdUnitId,
    magicWordSearch,
    entityType,
    sportsLeague,
    pod,
  });

  const sanitizedWinningAuction = demandParametersCardData?.winningAuction;
  const winningAuctionToRender =
    sanitizedWinningAuction?.length > 1 ? sanitizedWinningAuction.join(', ') : sanitizedWinningAuction;

  return (
    <StyledDrawer
      placement="right"
      open={isOpen}
      onClose={onClose}
      destroyOnClose
      title={
        <span>
          Parameters applied: <span className="drawer-title-text">{isLoading ? 'loading...' : name}</span>
        </span>
      }
      width={600}
      closeIcon={<StyledCloseButton variant="outlined" icon="IconClose" />}
    >
      <AuctionErrorBoundary>
        {error ? (
          <Alert type="error" message={error} />
        ) : (
          <StyledContent>
            <div>
              <Skeleton paragraph={{ rows: 28 }} loading={isLoading}>
                <Card title="Snapshot Parameters">
                  <CardItem title="Selected Type" data={getLabelByValue(profileTypes, selectionSearch?.selectType)} />
                  <CardItem title={FORM_FIELDS.APP_NAME.label} data={appName} />
                  <CardItem title={FORM_FIELDS.PUBLISHER.label} data={publisher} />
                  <CardItem title={FORM_FIELDS.DISTRIBUTION_PARTNER.label} data={distributionPartner} />
                  <CardItem title={FORM_FIELDS.DISTRIBUTOR.label} data={distributor} />
                  <CardItem title={FORM_FIELDS.MARKET.label} data={market?.join(', ')} />
                  <CardItem title={FORM_FIELDS.SOVEREIGN_COUNTRIES.label} data={sovereignCountries?.join(', ')} />
                  <CardItem title={FORM_FIELDS.POD_POSITION.label} data={podPosition} />
                  <CardItem title="Device ID / SWID ID / Profile ID / Account ID " data={selectionSearch?.inputValue} />
                  <CardItem title="Session ID" data={sessionId} />
                  <CardItem title="Experiment ID" data={experimentId} />
                  <CardItem
                    title={FORM_FIELDS.RESULT.label}
                    data={getLabelByValue(RESULT_OPTIONS, result?.toLowerCase())}
                  />
                  <CardItem title="Status" data={status} />
                  <CardItem title="Date" data={formatDate(date)} />
                  <CardItem
                    title={FORM_FIELDS.TIME_ALLOWED.label}
                    data={getLabelByValue(TIME_FRAMES, timeAllowedBeforeStopRunning)}
                  />
                </Card>
                {!isEmpty(appParametersCardData) && (
                  <Card title="Device Parameters">
                    <CardItem title={FORM_FIELDS.DOMAIN.label} data={domain} />
                    <CardItem
                      title={FORM_FIELDS.INVENTORY_TYPE.label}
                      data={getLabelByValue(INVENTORY_TYPES, _capitalize(inventoryType))}
                    />
                    <CardItem title={FORM_FIELDS.DEVICE_FAMILY.label} data={deviceFamily} />
                    <CardItem title={FORM_FIELDS.BRAND.label} data={brand} />
                    <CardItem title={FORM_FIELDS.FORM_FACTOR.label} data={formFactor} />
                    <CardItem title={FORM_FIELDS.DEVICE_CATEGORY.label} data={deviceCategory} />
                    <CardItem title={FORM_FIELDS.IFA_TYPE.label} data={ifaType} />
                    <CardItem title={FORM_FIELDS.USER_AGENT.label} data={userAgent} />
                    <CardItem
                      title={FORM_FIELDS.LIMITED_AD_TRACKING.label}
                      data={getLabelByValue(LIMITED_AD_TRACKING_OPTIONS, dnt)}
                    />
                    <CardItem title={FORM_FIELDS.DEVICE_IP.label} data={ipAddress} />
                  </Card>
                )}
                {!isEmpty(contentParametersCardData) && (
                  <Card title="Content Parameters">
                    <CardItem title={FORM_FIELDS.CHANNEL.label} data={channel} />
                    <CardItem title={FORM_FIELDS.SERIES.label} data={series} />
                    <CardItem title={FORM_FIELDS.ALID_ID.label} data={assetId} />
                    <CardItem title={FORM_FIELDS.CONTENT_PARTNER.label} data={contentPartner} />
                    <CardItem title={FORM_FIELDS.VDM.label} data={vdm} />
                    <CardItem title={FORM_FIELDS.ENTITY_TYPE.label} data={entityType} />
                    <CardItem title={FORM_FIELDS.SPORTS_LEAGUE.label} data={sportsLeague} />
                    <CardItem title={FORM_FIELDS.SUPPLY_AD_UNIT_ID.label} data={supplyAdUnitId} />
                    <CardItem
                      title={FORM_FIELDS.MAGIC_WORDS_TYPE.label}
                      data={getLabelByValue(MAGIC_WORDS_OPTIONS, magicWordSearch?.type)}
                    />
                    <CardItem title={FORM_FIELDS.POD.label} data={pod} />
                  </Card>
                )}
                {!isEmpty(demandParametersCardData) && (
                  <Card title="Demand Parameters">
                    <CardItem title={FORM_FIELDS.WINNING_AUCTION.label} data={winningAuctionToRender} />
                    <CardItem title={FORM_FIELDS.ADVERTISER.label} data={advertiser} />
                    <CardItem title="DSP : CRID" data={creativeId} />
                    <CardItem title={FORM_FIELDS.AD_PRODUCT.label} data={adProduct} />
                    <CardItem title={FORM_FIELDS.AD_PRODUCT_TYPE.label} data={adProductType} />
                    <CardItem title={FORM_FIELDS.AD_PARTNER_DSP.label} data={adPartnerDsp} />
                    <CardItem title={FORM_FIELDS.BUYER_DEAL_ID.label} data={externalDealId} />
                    <CardItem title={FORM_FIELDS.SYSTEM_DEAL_ID.label} data={internalDealId} />
                    <CardItem title={FORM_FIELDS.BID_STATE.label} data={bidState} />
                    <CardItem
                      title={FORM_FIELDS.CREATIVE_DURATION.label}
                      data={
                        creativeDuration &&
                        DURATION_OPTIONS.filter((item) => creativeDuration.includes(item.value)).map((item, index) => {
                          return (index ? ' ' : '') + item.label;
                        }) + ' seconds'
                      }
                    />
                    <CardItem title={FORM_FIELDS.MEDIA_FILE_URL.label} data={mediaFileUrl} />
                    <CardItem title="Received Bid Deal Type" data={receivedBidDealId} />
                  </Card>
                )}
              </Skeleton>
            </div>
            <div className="action-rerun">
              <Button
                icon="IconCopy"
                size="medium"
                variant="outlined"
                onClick={() => {
                  onRerunSnapshot({ id: snapshotId });
                  onClose();
                }}
              >
                Rerun Snapshot
              </Button>
            </div>
          </StyledContent>
        )}
      </AuctionErrorBoundary>
    </StyledDrawer>
  );
};
